import type { SolanaWithdrawalQuote } from "../../joinsplit-v2/solana/fees";
import {
  quoteSolanaSwap,
  executeSolanaSwap,
} from "../../joinsplit-v2/solana/swap";
import type { SolanaSwapApproval } from "../../joinsplit-v2/solana/swapQuote";
import { solanaSendAmounts } from "../helpers/amounts";
import { useCallback, useEffect, useRef, useState } from "react";
import { solanaPubkeyBase58 } from "@stealthos/sdk";
import {
  formatSolanaTokenAmount,
  parseSolanaTokenAmount,
  type SolanaNetworkConfig,
  type SolanaTokenInfo,
} from "../../config/solana";
import type { JoinSplitNote } from "../../joinsplit/types";
import {
  executeSolanaBatchWithdraw,
  executeSolanaDeposit,
  executeSolanaMigration,
  executeSolanaTransfer,
  executeSolanaWithdraw,
  getSolanaJournalRecord,
  signSolanaTransaction,
  withSolanaWalletLock,
  type SolanaOperationResult,
  type SolanaRpc,
  type SolanaSettlementContext,
} from "../../joinsplit-v2/solana";
import { parsePrivateAddress } from "../../utils/inbox-crypto";
import { solanaRecipientInboxKey } from "../../joinsplit-v2/solana/recipient";
import type { AppStatusMessage } from "../../app-shell";
import type { SolanaLogType, SolanaWalletState } from "../types";
import type { useSolanaNotes } from "./useSolanaNotes";
import type { SolanaProxyAuthRequestOptions } from "./useSolanaProxyAuth";
import {
  isSolanaHandleInput,
  resolveSolanaHandleRecipient,
} from "./useSolanaHandleRecipient";

export type SolanaRecipientKind = "private" | "wallet" | "invalid";

/** A private address (`mpk:inbox`) transfers inside the pool; a wallet address withdraws. */
export function classifySolanaRecipient(value: string): SolanaRecipientKind {
  const trimmed = value.trim();
  if (!trimmed) return "invalid";
  const privateAddress = parsePrivateAddress(trimmed);
  if (privateAddress)
    return solanaRecipientInboxKey(privateAddress.inboxPublicKeyHex)
      ? "private"
      : "invalid";
  try {
    solanaPubkeyBase58(trimmed, "recipient");
    return "wallet";
  } catch {
    return "invalid";
  }
}

interface SolanaBatchRow {
  owner: string;
  amount: string;
}

function friendly(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/user rejected|rejected the request|cancelled|canceled/i.test(message)) {
    return "Request rejected in the wallet";
  }
  return message
    .replace(/^Solana JoinSplit V2 wallet: /, "")
    .replace(/^Flat JoinSplit V2 wallet: /, "");
}

export function useSolanaActions({
  network,
  rpc,
  wallet,
  notes,
  ensureProxyAuthToken,
  addLog,
  setStatus,
}: {
  network: SolanaNetworkConfig;
  rpc: SolanaRpc;
  wallet: SolanaWalletState;
  notes: ReturnType<typeof useSolanaNotes>;
  ensureProxyAuthToken: (
    options?: SolanaProxyAuthRequestOptions,
  ) => Promise<string>;
  addLog: (message: string, type?: SolanaLogType, signature?: string) => void;
  setStatus: (status: AppStatusMessage | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const activeRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      activeRef.current?.abort();
      activeRef.current = null;
    },
    [wallet.address, wallet.spendingKeys, network.key, network.programId],
  );

  const settlementContext = useCallback(
    (signal: AbortSignal): SolanaSettlementContext => {
      if (!wallet.spendingKeys || !notes.journalScope) {
        throw new Error("Connect a wallet first");
      }
      return {
        rpc,
        network,
        spendingKeys: wallet.spendingKeys,
        journalScope: notes.journalScope,
        backupJournal: notes.backupJournal,
        relay: notes.relay,
        onLog: addLog,
        signal,
        cache: notes.cache,
      };
    },
    [
      addLog,
      network,
      notes.cache,
      notes.backupJournal,
      notes.journalScope,
      notes.relay,
      rpc,
      wallet.spendingKeys,
    ],
  );

  const run = useCallback(
    async <T>(
      label: string,
      work: (signal: AbortSignal) => Promise<T>,
    ): Promise<T | null> => {
      if (!wallet.connected || !wallet.spendingKeys) {
        setStatus({ type: "error", message: "Connect a Solana wallet first" });
        return null;
      }
      if (busy || activeRef.current) {
        setStatus({
          type: "error",
          message: "Another private action is still running",
        });
        return null;
      }
      const controller = new AbortController();
      activeRef.current = controller;
      setBusy(true);
      setStatus({ type: "info", message: `${label}…` });
      try {
        return await withSolanaWalletLock({
          networkKey: network.key,
          programId: network.programId,
          account: wallet.address,
          signal: controller.signal,
          run: () => work(controller.signal),
        });
      } catch (error) {
        const message = friendly(error);
        addLog(`${label} failed: ${message}`, "error");
        setStatus({ type: "error", message: `${label} failed: ${message}` });
        return null;
      } finally {
        if (activeRef.current === controller) activeRef.current = null;
        setBusy(false);
      }
    },
    [
      addLog,
      busy,
      network.key,
      network.programId,
      setStatus,
      wallet.address,
      wallet.connected,
      wallet.spendingKeys,
    ],
  );

  const afterSettlement = useCallback(
    async (
      result: SolanaOperationResult,
      history: {
        type: "transfer" | "withdraw" | "swap";
        amount: string;
        symbol: string;
        memo?: string;
      },
    ) => {
      if (!result.success) throw new Error(result.error || "settlement failed");
      let deliveryPending = true;
      try {
        await notes.markSpentLocally(
          result.spentNotes.map((note) => note.commitment),
        );
        if (result.journalId && notes.journalScope) {
          const record = await getSolanaJournalRecord(
            notes.journalScope,
            result.journalId,
          );
          if (
            record?.signature === result.signature &&
            record.outputNotes.length === result.outputNotes.length &&
            record.outputNotes.every(
              (note, index) =>
                note.commitment.toLowerCase() ===
                result.outputNotes[index].commitment.toLowerCase(),
            )
          ) {
            const delivery = await notes.completeDeliveries([record]);
            deliveryPending = delivery.pendingRecordIds.length > 0;
          }
        }
        await notes.addHistoryEntry({ ...history, txHash: result.signature });
      } catch (error) {
        addLog(
          `Settlement confirmed; local recovery or note delivery is pending: ${friendly(error)}`,
          "warning",
          result.signature,
        );
      }
      void notes.syncNotes();
      return deliveryPending;
    },
    [addLog, notes],
  );

  const handleShield = useCallback(
    async (
      amountInput: string,
      token: SolanaTokenInfo,
      memo?: string,
      recipientAddress?: string,
    ) => {
      const connection = wallet.connection;
      if (!connection) return;
      let amount: bigint;
      try {
        amount = parseSolanaTokenAmount(amountInput || "0", token.decimals);
      } catch (error) {
        setStatus({ type: "error", message: friendly(error) });
        return;
      }
      if (amount <= 0n) {
        setStatus({ type: "error", message: "Enter a valid amount" });
        return;
      }
      const recipient =
        recipientAddress === undefined
          ? null
          : parsePrivateAddress(recipientAddress);
      if (
        recipientAddress !== undefined &&
        (!recipient || !solanaRecipientInboxKey(recipient.inboxPublicKeyHex))
      ) {
        setStatus({
          type: "error",
          message:
            "Payment requires a valid recipient private address and inbox key",
        });
        return;
      }
      const isSelf =
        !recipient || BigInt(recipient.mpkHex) === wallet.spendingKeys?.mpk;
      const result = await run(
        recipient ? "Payment" : "Shielding",
        async (signal) => {
          await ensureProxyAuthToken({ interactive: true });
          let recoveredNotes: typeof notes.notes = [];
          if (isSelf) {
            addLog(
              "Deposit recovery started: checking existing notes and transaction finality",
              "info",
            );
            const recovered = await notes.syncNotes(undefined, undefined, {
              forDeposit: true,
              mint: token.mint,
            });
            if (!recovered || signal.aborted)
              throw new Error(
                "Deposit paused: recovery did not complete; refresh recovery before retrying",
              );
            recoveredNotes = recovered.notes;
          }
          const deposit = await executeSolanaDeposit({
            rpc,
            network,
            signer: {
              address: connection.address,
              publicKey: connection.publicKey,
              signTransaction: (bytes) =>
                signSolanaTransaction(connection, bytes),
            },
            spendingKeys: wallet.spendingKeys!,
            notes: recoveredNotes,
            cache: notes.cache,
            recipientMpk: recipient ? BigInt(recipient.mpkHex) : undefined,
            recipientInboxPublicKeyHex: recipient?.inboxPublicKeyHex,
            mint: token.mint,
            amount,
            journalScope: notes.journalScope!,
            backupJournal: notes.backupJournal,
            privateHistory: {
              type: "deposit",
              amount: formatSolanaTokenAmount(amount, token.decimals),
              symbol: token.symbol,
              memo,
              timestamp: Date.now(),
            },
            onLog: addLog,
            signal,
          });
          let deliveryPending = true;
          try {
            if (deposit.spentNote) {
              await notes.markSpentLocally([deposit.spentNote.commitment]);
            }
            if (isSelf)
              await notes.rememberLocalNotes([deposit.note], deposit.signature);
            const record = await getSolanaJournalRecord(
              notes.journalScope!,
              deposit.journalId,
            );
            if (
              record?.signature === deposit.signature &&
              record.outputNotes[0]?.commitment.toLowerCase() ===
                deposit.note.commitment.toLowerCase()
            ) {
              deliveryPending =
                (await notes.completeDeliveries([record])).pendingRecordIds
                  .length > 0;
            }
            await notes.addHistoryEntry({
              type: "deposit",
              amount: formatSolanaTokenAmount(amount, token.decimals),
              symbol: token.symbol,
              memo,
              txHash: deposit.signature,
            });
          } catch (error) {
            addLog(
              `Deposit confirmed; local recovery or note delivery is pending: ${friendly(error)}`,
              "warning",
              deposit.signature,
            );
          }
          return { deposit, deliveryPending };
        },
      );
      if (result) {
        setStatus({
          type: result.deliveryPending ? "info" : "success",
          message: result.deliveryPending
            ? `Deposit settled; note delivery is pending. Keep this wallet connected and Sync.`
            : recipient
              ? `Paid ${formatSolanaTokenAmount(amount, token.decimals)} ${token.symbol} into the recipient's private balance`
              : `Shielded ${formatSolanaTokenAmount(amount, token.decimals)} ${token.symbol}`,
        });
        void notes.syncNotes();
      }
    },
    [
      addLog,
      ensureProxyAuthToken,
      network,
      notes,
      rpc,
      run,
      setStatus,
      wallet.connection,
      wallet.spendingKeys,
    ],
  );

  const handleSend = useCallback(
    async (params: {
      recipient: string;
      resolvedPrivateAddress?: string;
      amountInput: string;
      feeQuote?: SolanaWithdrawalQuote;
      token: SolanaTokenInfo;
      memo?: string;
    }) => {
      const isHandle = isSolanaHandleInput(params.recipient);
      const destination = isHandle
        ? (params.resolvedPrivateAddress ?? "")
        : params.recipient;
      const kind = classifySolanaRecipient(destination);
      if (kind === "invalid") {
        setStatus({
          type: "error",
          message:
            "Enter a StealthOS private address or a Solana wallet address",
        });
        return;
      }
      let amount: bigint;
      try {
        amount = parseSolanaTokenAmount(
          params.amountInput || "0",
          params.token.decimals,
        );
      } catch (error) {
        setStatus({ type: "error", message: friendly(error) });
        return;
      }
      if (amount <= 0n) {
        setStatus({ type: "error", message: "Enter a valid amount" });
        return;
      }
      const preview = solanaSendAmounts(
        network,
        params.token,
        [params.amountInput],
        notes.balances.find((row) => row.mint === params.token.mint)
          ?.spendable ?? 0n,
        kind,
        params.feeQuote ? BigInt(params.feeQuote.quote.fee) : null,
      );
      if (preview.error) {
        setStatus({ type: "error", message: preview.error });
        return;
      }
      const display = formatSolanaTokenAmount(amount, params.token.decimals);
      const ok = await run(
        kind === "private" ? "Private transfer" : "Withdrawal",
        async (signal) => {
          const ctx = settlementContext(signal);
          if (
            isHandle &&
            (await resolveSolanaHandleRecipient(
              params.recipient,
              network.key,
              signal,
            )) !== destination
          )
            throw new Error(
              "This handle's receiving address changed. Review the recipient again.",
            );
          if (kind === "private") {
            const parsed = parsePrivateAddress(destination)!;
            const recipientMpk = BigInt(parsed.mpkHex);
            const result = await executeSolanaTransfer({
              ctx,
              notes: notes.notes,
              mint: params.token.mint,
              recipientMpk,
              recipientInboxPublicKeyHex: parsed.inboxPublicKeyHex,
              recipientPrivateHistory: {
                type: "receive",
                amount: display,
                symbol: params.token.symbol,
                memo: params.memo,
                timestamp: Date.now(),
              },
              amount,
            });
            const deliveryPending = await afterSettlement(result, {
              type: "transfer",
              amount: display,
              symbol: params.token.symbol,
              memo: params.memo,
            });
            return { deliveryPending };
          }
          const result = await executeSolanaWithdraw({
            ctx,
            notes: notes.notes,
            mint: params.token.mint,
            recipientOwner: params.recipient.trim(),
            feeQuote: params.feeQuote,
            amount,
          });
          const deliveryPending = await afterSettlement(result, {
            type: "withdraw",
            amount: display,
            symbol: params.token.symbol,
            memo: params.memo,
          });
          return { deliveryPending };
        },
      );
      if (ok) {
        setStatus({
          type: ok.deliveryPending ? "info" : "success",
          message: ok.deliveryPending
            ? kind === "private"
              ? "Transfer settled; recipient note delivery is pending. Keep this wallet connected and Sync."
              : "Withdrawal settled; change note delivery is pending. Keep this wallet connected and Sync."
            : kind === "private"
              ? `Sent ${display} ${params.token.symbol} privately`
              : `Withdrew ${formatSolanaTokenAmount(preview.received, params.token.decimals)} ${params.token.symbol} to ${params.recipient.trim().slice(0, 6)}…`,
        });
      }
    },
    [
      afterSettlement,
      network,
      notes.balances,
      notes.notes,
      run,
      setStatus,
      settlementContext,
    ],
  );

  const handleBatchWithdraw = useCallback(
    async (
      rows: SolanaBatchRow[],
      token: SolanaTokenInfo,
      memo?: string,
      feeQuote?: SolanaWithdrawalQuote,
    ) => {
      let items: Array<{ owner: string; amount: bigint }>;
      try {
        items = rows.map((row) => {
          if (classifySolanaRecipient(row.owner) !== "wallet") {
            throw new Error(`"${row.owner}" is not a Solana wallet address`);
          }
          const amount = parseSolanaTokenAmount(row.amount, token.decimals);
          if (amount <= 0n)
            throw new Error(
              `amount for ${row.owner.slice(0, 6)}… must be positive`,
            );
          return { owner: row.owner.trim(), amount };
        });
      } catch (error) {
        setStatus({ type: "error", message: friendly(error) });
        return;
      }
      if (items.length === 0) {
        setStatus({ type: "error", message: "Add at least one recipient" });
        return;
      }
      const preview = solanaSendAmounts(
        network,
        token,
        rows.map((row) => row.amount),
        notes.balances.find((row) => row.mint === token.mint)?.spendable ?? 0n,
        "batch",
        feeQuote ? BigInt(feeQuote.quote.fee) : null,
      );
      if (preview.error) {
        setStatus({ type: "error", message: preview.error });
        return;
      }
      const total = items.reduce((sum, item) => sum + item.amount, 0n);
      const display = formatSolanaTokenAmount(total, token.decimals);
      const ok = await run("Batch withdrawal", async (signal) => {
        const result = await executeSolanaBatchWithdraw({
          ctx: settlementContext(signal),
          notes: notes.notes,
          mint: token.mint,
          items,
          feeQuote,
        });
        const deliveryPending = await afterSettlement(result, {
          type: "withdraw",
          amount: display,
          symbol: token.symbol,
          memo: memo || `${items.length} recipients`,
        });
        return { deliveryPending };
      });
      if (ok) {
        setStatus({
          type: ok.deliveryPending ? "info" : "success",
          message: ok.deliveryPending
            ? "Batch withdrawal settled; change note delivery is pending. Keep this wallet connected and Sync."
            : `Paid ${items.length} recipients ${display} ${token.symbol}`,
        });
      }
    },
    [
      afterSettlement,
      network,
      notes.balances,
      notes.notes,
      run,
      setStatus,
      settlementContext,
    ],
  );

  const handleMigrate = useCallback(
    async (sourceTreeIndex: number, token: SolanaTokenInfo) => {
      const ok = await run("Migration", async (signal) => {
        const result = await executeSolanaMigration({
          ctx: settlementContext(signal),
          notes: notes.notes,
          mint: token.mint,
          sourceTreeIndex,
        });
        if (!result.success)
          throw new Error(result.error || "migration failed");
        let deliveryPending = true;
        try {
          await notes.markSpentLocally(
            result.spentNotes.map((note) => note.commitment),
          );
          await notes.rememberLocalNotes(
            result.outputNotes as JoinSplitNote[],
            result.signature,
          );
          if (result.journalId && notes.journalScope) {
            const record = await getSolanaJournalRecord(
              notes.journalScope,
              result.journalId,
            );
            if (
              record?.signature === result.signature &&
              record.outputNotes.length === result.outputNotes.length &&
              record.outputNotes.every(
                (note, index) =>
                  note.commitment.toLowerCase() ===
                  result.outputNotes[index].commitment.toLowerCase(),
              )
            ) {
              deliveryPending =
                (await notes.completeDeliveries([record])).pendingRecordIds
                  .length > 0;
            }
          }
        } catch (error) {
          addLog(
            `Migration confirmed; local recovery or note delivery is pending: ${friendly(error)}`,
            "warning",
            result.signature,
          );
        }
        void notes.syncNotes();
        return { deliveryPending };
      });
      if (ok) {
        setStatus({
          type: ok.deliveryPending ? "info" : "success",
          message: ok.deliveryPending
            ? "Migration settled; note delivery is pending. Keep this wallet connected and Sync."
            : `Migrated tree ${sourceTreeIndex} notes to the current tree`,
        });
      }
    },
    [notes, run, setStatus, settlementContext],
  );

  const cancelActive = useCallback(() => {
    activeRef.current?.abort();
  }, []);

  const getSwapQuote = useCallback(
    async (
      mint: string | null,
      outputMint: string | null,
      amount: bigint,
      slippageBps: number,
      signal: AbortSignal,
    ) => {
      // Typing an amount must not trigger a wallet signature prompt.
      await ensureProxyAuthToken({ interactive: false });
      return quoteSolanaSwap({
        ctx: settlementContext(signal),
        notes: notes.notes,
        mint,
        outputMint,
        amount,
        slippageBps,
      });
    },
    [ensureProxyAuthToken, settlementContext, notes.notes],
  );

  const handleSwap = useCallback(
    async (approved: SolanaSwapApproval) => {
      return run("Swapping through Orca", async (signal) => {
        const input = network.tokens.find(
          (t) => t.mint === approved.quote.request.mint,
        );
        const output = network.tokens.find(
          (t) => t.mint === approved.quote.request.outputMint,
        );
        if (!input || !output) throw new Error("Unsupported swap pair");
        const result = await executeSolanaSwap({
          ctx: settlementContext(signal),
          notes: notes.notes,
          mint: input.mint,
          approved,
        });
        const pending = await afterSettlement(result, {
          type: "swap",
          amount: formatSolanaTokenAmount(
            BigInt(approved.quote.request.amount),
            input.decimals,
          ),
          symbol: input.symbol,
        });
        const received = result.outputNotes[result.outputNotes.length - 1];
        setStatus({
          type: pending ? "info" : "success",
          message: pending
            ? "Swap settled; output note delivery is pending. Keep this wallet connected and Sync."
            : `Received ${formatSolanaTokenAmount(received.value, output.decimals)} private ${output.symbol} through Orca`,
        });
        return result;
      });
    },
    [
      run,
      network.tokens,
      settlementContext,
      notes.notes,
      afterSettlement,
      setStatus,
    ],
  );

  return {
    busy,
    handleShield,
    handleSend,
    handleBatchWithdraw,
    handleMigrate,
    getSwapQuote,
    handleSwap,
    cancelActive,
  };
}
